import IAppStore from "app/IAppStore";
import React, { useState } from "react";
import * as _ from "lodash";
import { Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as NotificationActions from "./notifications.actions";
import './NotificationCenter.scss';
import StoneHedgeNotification from "./stoneHedgeNotification";
import { formatRecentDate, truncateText } from "common/formatters";
import SiteLink from "common/ui/SiteLink";
import NavbarMenuToggle from "common/ui/NavbarMenuToggle";
import { getNotificationLink } from "./helpers";
import { listUnreadNotifications } from "./notifications.actions";
import NotificationModal from "./NotificationModal";
import useReduxAction from "common/redux/useReduxAction";
import ConditionalLink from "common/ui/ConditionalLink";
import { localizeCommon as localize } from "features/localization/localizeService";
import useStoreSelector from "common/redux/useStoreSelector";

const NotificationItem: React.FC<{ notification: StoneHedgeNotification, onHide(): void }> = (props) => {

    return <div className="notification" onClick={props.onHide}>
        <button className="close" onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            props.onHide();
        }}>
            <i className="material-icons">close</i>
        </button>
        <div className="title">{props.notification.title}</div>
        <div className="message">{truncateText(props.notification.message, 150)}</div>
        <div className="timestamp">{formatRecentDate(props.notification.createdDate)}</div>
    </div>;
}

const NotificationCenter: React.FC = () => {
    const maxNotificationsToShow = 20;
    const dispatch = useDispatch();

    const [showSettingsModal, setShowSettingsModal] = useState(false);

    const allNotifications = useSelector((store: IAppStore) => store.notifications);

    const unreadNotifications = _.orderBy(allNotifications.notifications.filter(m => !m.isRead), m => m.createdDate, 'desc')
        .slice(0, maxNotificationsToShow);

    useReduxAction(dispatch => {
        const allUnread = allNotifications.notifications.filter(m => !m.isRead);

        if (allUnread.length < maxNotificationsToShow && allNotifications.unreadCount > allUnread.length) {
            //Load an extra page so we aren't too chatty
            listUnreadNotifications(dispatch, maxNotificationsToShow * 2, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allNotifications]);

    function markNotificationAsRead(notification: StoneHedgeNotification) {
        if (notification.isRead) return;
        NotificationActions.markNotificationRead(dispatch, notification);
    }

    function markAllAsRead() {
        NotificationActions.markAllNotificationsRead(dispatch);
    }

    const unreadCount = unreadNotifications?.length ?? 0;

    return <>
        <Dropdown className="notification-center">
            <Dropdown.Toggle as={NavbarMenuToggle} id="notifications-toggle">
                <i className="material-icons" data-testid="notifications-icon">notifications</i>
                {unreadCount > 0 && <span className="unread" />}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Header>
                    <div className="container p-0">
                        <div className="col-6">
                            <div className="menu-header">
                                {localize("NotificationCenter_Header_Notifications")}
                                <button className="btn btn-link pl-1" onClick={() => setShowSettingsModal(true)}>
                                    <i className="material-icons">settings</i>
                                </button>
                            </div>
                        </div>
                        {unreadCount > 0 && <>
                            <div className="col-6 m-0 text-right">
                                <button className="btn btn-link btn-link-normal ml-auto p-0"
                                    onClick={() => markAllAsRead()}>
                                    {localize("NotificationCenter_Button_MarkAllRead")}
                                </button>
                            </div>
                        </>}
                    </div>
                </Dropdown.Header>
                <Dropdown.Divider />
                <div className="notification-scroll">
                    {unreadCount === 0 &&
                        <Dropdown.Item>{localize("NotificationCenter_Label_NoNewNotifications")}</Dropdown.Item>
                    }
                    {unreadNotifications.map((m, i) => <React.Fragment key={m.id}>
                        {i > 0 && <Dropdown.Divider />}
                        {i < maxNotificationsToShow &&
                            <NotificationRow notification={m}
                                onRead={() => markNotificationAsRead(m)}
                            />
                        }
                    </React.Fragment>
                    )}
                    {unreadCount > maxNotificationsToShow &&
                        <Dropdown.Item><SiteLink.Notifications>{localize("NotificationCenter_Button_ViewMoreNotifications")}</SiteLink.Notifications></Dropdown.Item>
                    }
                </div>
                <Dropdown.Item href={SiteLink.UrlResolver.forNotifications()} className="footer">
                    <div className="btn btn-link">{localize("NotificationCenter_Button_ViewAllNotifications")}</div>
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>

        {showSettingsModal && <NotificationModal onClose={() => setShowSettingsModal(false)} />}
    </>;
}


type NotificationRowProps = {
    notification: StoneHedgeNotification;
    onRead(): void;
}
const NotificationRow: React.FC<NotificationRowProps> = (props) => {
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const linkDetails = getNotificationLink(props.notification.link, isCustomer);

    return <ConditionalLink to={linkDetails.url}
        isExternal={linkDetails.isExternal}
        className="no-link dropdown-item"
        onClick={() => props.onRead()}>
        <NotificationItem
            notification={props.notification}
            onHide={() => props.onRead()}
        />
    </ConditionalLink>;
}

export default NotificationCenter;